export const getRowHeight = ({ pageHeight, scale, gap }) => {
  if (!pageHeight) return 0;

  return pageHeight * scale + gap;
};

export const getPageHeightsWithFallback = ({
  pageDimensions = [],
  fallbackHeight = 0,
}) => {
  return pageDimensions.map((dimensions) => {
    return dimensions?.height || fallbackHeight;
  });
};

export const getMaxPageWidthWithFallback = ({
  pageDimensions = [],
  fallbackWidth = 0,
}) => {
  const maxWidth = pageDimensions.reduce((max, dimensions) => {
    return Math.max(max, dimensions?.width || fallbackWidth);
  }, 0);

  return maxWidth || fallbackWidth;
};

export const getPageScrollMetrics = ({
  pageDimensions = [],
  fallbackDimensions,
}) => {
  const fallbackHeight = fallbackDimensions?.height || 0;
  const fallbackWidth = fallbackDimensions?.width || 0;

  return {
    pageHeights: getPageHeightsWithFallback({
      pageDimensions,
      fallbackHeight,
    }),
    maxPageWidth: getMaxPageWidthWithFallback({
      pageDimensions,
      fallbackWidth,
    }),
  };
};

export const getOffsetMeta = (scrollTop, rowHeights = []) => {
  let cumulativeTop = 0;

  for (let index = 0; index < rowHeights.length; index += 1) {
    const rowHeight = rowHeights[index];
    const nextTop = cumulativeTop + rowHeight;

    if (scrollTop < nextTop) {
      return {
        rowIndex: index,
        offsetInRow: scrollTop - cumulativeTop,
        rowHeight,
      };
    }

    cumulativeTop = nextTop;
  }

  const lastIndex = Math.max(rowHeights.length - 1, 0);
  const lastHeight = rowHeights[lastIndex] || 0;

  return {
    rowIndex: lastIndex,
    offsetInRow: 0,
    rowHeight: lastHeight,
  };
};

export const getCumulativeHeightUntil = (rowHeights = [], endIndex = 0) => {
  let total = 0;

  for (let index = 0; index < endIndex; index += 1) {
    total += rowHeights[index] || 0;
  }

  return total;
};

export const getScaledRowHeights = (heights = [], scaleValue = 1, rowGap = 0) =>
  heights.map((height) => height * scaleValue + rowGap);

export const computeVariableScrollFromScale = ({
  scrollTop,
  pageHeights = [],
  prevScale,
  nextScale,
  gap,
}) => {
  if (!pageHeights.length) {
    return {
      rowIndex: 0,
      targetScroll: scrollTop,
    };
  }

  const oldRowHeights = getScaledRowHeights(pageHeights, prevScale, gap);
  const newRowHeights = getScaledRowHeights(pageHeights, nextScale, gap);

  const { rowIndex, offsetInRow, rowHeight } = getOffsetMeta(
    scrollTop,
    oldRowHeights
  );
  const offsetRatio = rowHeight ? offsetInRow / rowHeight : 0;
  const newOffsetInRow = offsetRatio * (newRowHeights[rowIndex] || 0);
  const targetScroll =
    getCumulativeHeightUntil(newRowHeights, rowIndex) + newOffsetInRow;

  return {
    rowIndex,
    targetScroll,
  };
};

export const computeHorizontalScrollFromScale = ({
  pageWidth,
  containerWidth,
  prevScale,
  nextScale,
  scrollLeftBefore,
}) => {
  const oldScaledWidth = pageWidth * prevScale;
  const newScaledWidth = pageWidth * nextScale;
  const oldMaxScrollLeft = Math.max(0, oldScaledWidth - containerWidth);
  const newMaxScrollLeft = Math.max(0, newScaledWidth - containerWidth);

  if (newMaxScrollLeft <= 0) {
    return 0;
  }

  if (oldMaxScrollLeft <= 0) {
    return Math.min(scrollLeftBefore, newMaxScrollLeft);
  }

  const scrollRatio = scrollLeftBefore / oldMaxScrollLeft;

  return Math.min(
    newMaxScrollLeft,
    Math.max(0, scrollRatio * newMaxScrollLeft)
  );
};
